import React, { createContext, useState, useContext, useRef, useCallback } from "react";
import { QXWSMgr } from "@coreapp/CoreApp/QXWSMgr";
import { QXDocMgr } from "@coreapp/CoreApp/QXDocMgr";
import { GetWSMgr } from "@/Utils/StateLib";
import { clone, cloneDeep } from "lodash";
import { GetChartMetadata, GetPlanSettings } from "@/Utils/Promises";
import { useAppNavigate } from "@/hooks/useAppNavigate";

export interface ChartConfig {
  ID: string;
  name: string;
  [key: string]: any;
}

export type ApprovalsSettingsKey = "enablePlanApprovals";

export type PlanSettingsData = {
  additionalFields: {
    holder: string[];
    position: string[];
  };
  approvalsSettings: Record<ApprovalsSettingsKey, boolean>;
};

const DEFAULT_PLAN_SETTINGS: PlanSettingsData = {
  additionalFields: { holder: [], position: [] },
  approvalsSettings: { enablePlanApprovals: false }
};

interface PlanSettingsContextType {
  chartConfig: Nullable<ChartConfig>;
  settings: PlanSettingsData;
  isLoading: boolean;
  isSaving: boolean;
  updateChartConfig: (nextConfigData: Nullable<ChartConfig>) => void;
  updateSettings: (nextSettings: Partial<NonNullable<PlanSettingsData>>) => void;
  getClonedSettings: () => Nullable<PlanSettingsData>;
  updateClonedSettings: (nextSettings: PlanSettingsData) => void;
  loadChartMetadata: (baseChartId: string) => void;
  getMockWsMgr: () => QXWSMgr | null;
  launchPlanSettings: (chartID: string, tab: string, options?: { replace?: boolean }) => void;
  updateIsSaving: (isSaving: boolean) => void;
}

const PlanSettingsContext = createContext<PlanSettingsContextType | undefined>(undefined);

interface PlanSettingsProviderProps {
  children: React.ReactNode;
}

export const PlanSettingsProvider: React.FC<PlanSettingsProviderProps> = ({ children }) => {
  const [chartConfig, setChartConfig] = useState<Nullable<ChartConfig>>(null);
  const [settings, setSettings] = useState<PlanSettingsData>(DEFAULT_PLAN_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const nav = useAppNavigate();
  const clonedSettingsRef = useRef<Nullable<PlanSettingsData>>(null);
  const mockWsMgrRef = useRef<Nullable<QXWSMgr>>(null);

  const getMockWsMgr = () => {
    return mockWsMgrRef.current;
  };

  const updateMockWsMgr = (wsMgr: QXWSMgr) => {
    mockWsMgrRef.current = wsMgr;
  };

  const updateChartConfig = (nextConfigData: Nullable<ChartConfig>) => {
    setChartConfig(nextConfigData);
  };

  const setInitialSettings = (initialSettings: Nullable<PlanSettingsData>) => {
    setSettings(initialSettings || DEFAULT_PLAN_SETTINGS);
    clonedSettingsRef.current = cloneDeep(initialSettings || DEFAULT_PLAN_SETTINGS);
  };

  const updateSettings = (nextSettings: Partial<PlanSettingsData>) => {
    setSettings(prev => {
      return {
        ...prev,
        ...nextSettings
      };
    });
  };

  const getClonedSettings = () => {
    return clonedSettingsRef.current;
  };

  const updateIsSaving = (saving: boolean) => {
    setIsSaving(saving);
  };

  const updateClonedSettings = (nextSettings: PlanSettingsData) => {
    clonedSettingsRef.current = cloneDeep(nextSettings || DEFAULT_PLAN_SETTINGS);
  };

  const createMockWsMgr = useCallback((chartConfig: Nullable<ChartConfig>): QXWSMgr => {
    const wsMgr = GetWSMgr();
    if (!wsMgr) {
      throw new Error("WSMgr is not available!");
    }
    // Create a shallow clone of the WSMgr, with only updates to necessary properties
    const mockWsMgr = clone(wsMgr);
    const mockDocMgr = new QXDocMgr();
    mockDocMgr.InitDocMgr(mockWsMgr);
    mockWsMgr.fDocMgr = mockDocMgr;

    const mockDoc = mockWsMgr.InitializeDoc({}, false, false, true);
    mockDoc.LoadLiveChartMetadata(chartConfig);

    mockWsMgr.SetDoc(mockDoc);
    return mockWsMgr;
  }, []);

  const loadChartMetadata = useCallback((baseChartId: string) => {
    if (!baseChartId) {
      return;
    }
    const load = async () => {
      try {
        setIsLoading(true);
        const { result } = await GetChartMetadata(baseChartId);
        const { result: savedData } = await GetPlanSettings(baseChartId);
        updateChartConfig(result);
        setInitialSettings(savedData);
        updateMockWsMgr(createMockWsMgr(result));
      } catch (error) {
        console.error("Error loading chart metadata:", error);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const launchPlanSettings = useCallback(
    (baseChartID: string, tab: string, options?: { replace?: boolean }) => {
      if (options) {
        nav.navigateSettings("plan", baseChartID, tab, options);
      } else {
        nav.navigateSettings("plan", baseChartID, tab);
      }
    },
    [nav]
  );

  return (
    <PlanSettingsContext.Provider
      value={{
        chartConfig,
        settings,
        isLoading,
        isSaving,
        updateChartConfig,
        updateSettings,
        getClonedSettings,
        updateClonedSettings,
        loadChartMetadata,
        getMockWsMgr,
        launchPlanSettings,
        updateIsSaving
      }}
    >
      {children}
    </PlanSettingsContext.Provider>
  );
};

export const usePlanSettingsContext = (): PlanSettingsContextType => {
  const context = useContext(PlanSettingsContext);
  if (!context) {
    throw new Error("usePlanSettingsContext must be used within a PlanSettingsProvider");
  }
  return context;
};
